import type { Match } from '@uefadigital/panenka';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useGetMatchDomesticQuery, useGetMatchesInfoByTeamIdQuery } from 'api/fspApi';
import { LineUpsCarousel } from 'components/LineUpsCarousel';
import useIsDesktop from 'hooks/useIsDesktop';
import type { ConfigCompetition } from 'utils/config';
import { translateEntity } from 'utils/translateEntity';

interface PreviousLineUpsData {
  matchData: Match;
  fieldSize: {
    desktop: number;
    mobile: number;
    desktopJerseySize: number;
    mobileJerseySize: number;
  };
  isPDFPrint?: boolean;
  competition?: ConfigCompetition;
}

const sortMatchesByDate = (matches: Array<Match>, domesticMatches: Array<Match>) => {
  return [...matches, ...domesticMatches]
    .sort(
      (match, nextMatch) =>
        new Date(nextMatch?.kickOffTime?.date ?? '').getTime() - new Date(match?.kickOffTime?.date ?? '').getTime(),
    )
    .filter((match) => match.lineupStatus === 'TACTICAL_AVAILABLE')
    .slice(0, 3);
};

export const PreviousLineUps = ({ matchData, fieldSize, isPDFPrint = false, competition }: PreviousLineUpsData) => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const Title = isDesktop
    ? 'pk-font-size--xl2 pk-line-height--sd pk-font-style--normal pk-font--comp-bold'
    : 'pk-font-size--xl pk-line-height--md pk-font-style--normal pk-font--comp-bold';

  const matchDate = dayjs(matchData?.matchday?.dateFrom).format('YYYY-MM-DD');

  const {
    data: homeTeamMatchesDetails = [],
    error: homeTeamMatchesDetailsError,
    isLoading: homeTeamMatchesDetailsLoading,
  } = useGetMatchesInfoByTeamIdQuery({ teamId: matchData?.homeTeam?.id, toDate: matchDate });

  const {
    data: awayTeamMatchesDetails = [],
    error: awayTeamMatchesDetailsError,
    isLoading: awayTeamMatchesDetailsLoading,
  } = useGetMatchesInfoByTeamIdQuery({ teamId: matchData?.awayTeam?.id, toDate: matchDate });

  const {
    data: homeTeamDomesticMatchesDetails = [],
    error: homeTeamDomesticMatchesDetailsError,
    isLoading: homeTeamDomesticMatchesDetailsLoading,
  } = useGetMatchDomesticQuery({ teamId: matchData?.homeTeam?.id, toDate: matchDate });

  const {
    data: awayTeamDomesticMatchesDetails = [],
    error: awayTeamDomesticMatchesDetailsError,
    isLoading: awayTeamDomesticMatchesDetailsLoading,
  } = useGetMatchDomesticQuery({ teamId: matchData?.awayTeam?.id, toDate: matchDate });

  const recentHomeTeamMatches = sortMatchesByDate(homeTeamMatchesDetails, homeTeamDomesticMatchesDetails);

  const recentAwayTeamMatches = sortMatchesByDate(awayTeamMatchesDetails, awayTeamDomesticMatchesDetails);

  const homeTeamName = translateEntity(matchData?.homeTeam)?.displayName;
  const awayTeamName = translateEntity(matchData?.awayTeam)?.displayName;

  if (
    !!homeTeamMatchesDetailsError ||
    !!awayTeamMatchesDetailsError ||
    homeTeamMatchesDetailsLoading ||
    awayTeamMatchesDetailsLoading ||
    !!homeTeamDomesticMatchesDetailsError ||
    !!awayTeamDomesticMatchesDetailsError ||
    homeTeamDomesticMatchesDetailsLoading ||
    awayTeamDomesticMatchesDetailsLoading
  ) {
    return <div />;
  }

  return (
    <div>
      <div className={`${Title} pk-col pk-m--0 pk-col--span-12 pk-text--text-01 pk-pb--s pr-font-size--16`}>
        {t('previous_line_ups')}
      </div>
      <LineUpsCarousel
        matchData={recentHomeTeamMatches}
        teamName={homeTeamName ?? ''}
        competition={competition}
        fieldSize={fieldSize}
        isPDFPrint={isPDFPrint}
      />
      <div className="pr-page-break--before">
        <LineUpsCarousel
          matchData={recentAwayTeamMatches}
          teamName={awayTeamName ?? ''}
          competition={competition}
          fieldSize={fieldSize}
          isPDFPrint={isPDFPrint}
        />
      </div>
    </div>
  );
};
